/**
 * React Query Client Configuration
 *
 * This module provides React Query client configuration for the School iDB application:
 * - QueryClient factory function with sensible defaults
 * - Default query options (staleTime, gcTime, retry logic)
 * - Default mutation options
 * - Error handling integration with API error utilities
 * - QueryClientProvider wrapper component
 *
 * @module @schoolidb/api/client/queryClient
 */

import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';

import { isRetryableError, calculateRetryDelay, isApiError, isAuthError } from '../utils/index';
import type { ApiError } from '../types/index';

// ============================================================================
// Stale Time Constants
// ============================================================================

/**
 * Stale time configurations for different data types
 * These values determine how long data is considered fresh before refetching
 */
export const STALE_TIMES = {
  /** User profile data - relatively stable, 5 minutes */
  USER: 5 * 60 * 1000,
  /** School profile data - stable, 10 minutes */
  SCHOOL: 10 * 60 * 1000,
  /** School search results - moderate freshness, 2 minutes */
  SEARCH: 2 * 60 * 1000,
  /** Feed/conversation data - needs freshness, 1 minute */
  FEED: 1 * 60 * 1000,
  /** Static configuration data - very stable, 30 minutes */
  CONFIG: 30 * 60 * 1000,
  /** AI search results - cached longer, 15 minutes */
  AI_SEARCH: 15 * 60 * 1000,
  /** Reviews data - moderate freshness, 5 minutes */
  REVIEWS: 5 * 60 * 1000,
  /** Saved schools list - user-specific, 3 minutes */
  SAVED_SCHOOLS: 3 * 60 * 1000,
  /** Default stale time for unspecified queries */
  DEFAULT: 5 * 60 * 1000,
} as const;

/**
 * Garbage collection time configurations
 * These values determine how long inactive data is kept in cache
 */
export const GC_TIMES = {
  /** User data - keep for 30 minutes after becoming inactive */
  USER: 30 * 60 * 1000,
  /** School data - keep for 1 hour (frequently accessed) */
  SCHOOL: 60 * 60 * 1000,
  /** Search results - keep for 15 minutes */
  SEARCH: 15 * 60 * 1000,
  /** Feed data - keep for 10 minutes */
  FEED: 10 * 60 * 1000,
  /** Config data - keep for 1 hour */
  CONFIG: 60 * 60 * 1000,
  /** Default gc time */
  DEFAULT: 30 * 60 * 1000,
} as const;

// ============================================================================
// Retry Configuration
// ============================================================================

/**
 * Maximum number of retry attempts for failed queries
 */
export const MAX_RETRY_ATTEMPTS = 3;

/**
 * Base delay for retry backoff (in milliseconds)
 */
export const RETRY_BASE_DELAY = 1000;

/**
 * Maximum delay for retry backoff (in milliseconds)
 */
export const RETRY_MAX_DELAY = 30000;

/**
 * Custom retry function that uses isRetryableError from utils
 * Only retries on network errors, timeouts, and server errors
 *
 * @param failureCount - Number of times the query has failed
 * @param error - The error that caused the failure
 * @returns Whether to retry the query
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  // Don't retry if we've exceeded max attempts
  if (failureCount >= MAX_RETRY_ATTEMPTS) {
    return false;
  }

  // Don't retry authentication errors - user needs to re-authenticate
  if (isAuthError(error)) {
    return false;
  }

  // Only retry if the error is retryable (network, timeout, server errors)
  return isRetryableError(error);
}

/**
 * Custom retry delay function using exponential backoff with jitter
 *
 * @param attemptIndex - The current retry attempt (0-indexed)
 * @returns Delay in milliseconds before the next retry
 */
export function getRetryDelay(attemptIndex: number): number {
  return calculateRetryDelay(attemptIndex, RETRY_BASE_DELAY, RETRY_MAX_DELAY);
}

// ============================================================================
// Error Handlers
// ============================================================================

/**
 * Global error handler type for query/mutation errors
 */
export type GlobalErrorHandler = (error: ApiError) => void;

/**
 * Default global error handler configuration
 */
export interface QueryClientErrorHandlers {
  /** Called when a query fails after all retries */
  onQueryError?: GlobalErrorHandler;
  /** Called when a mutation fails */
  onMutationError?: GlobalErrorHandler;
  /** Called when an authentication error occurs */
  onAuthError?: () => void;
}

/**
 * Creates a query cache with error handling
 *
 * @param handlers - Error handler callbacks
 * @returns Configured QueryCache instance
 */
function createQueryCache(handlers?: QueryClientErrorHandlers): QueryCache {
  return new QueryCache({
    onError: (error, query) => {
      // Log error in development
      if (process.env.NODE_ENV === 'development') {
        console.error(`[QueryCache] Query failed:`, {
          queryKey: query.queryKey,
          error,
        });
      }

      // Handle authentication errors
      if (isAuthError(error) && handlers?.onAuthError) {
        handlers.onAuthError();
        return;
      }

      // Call custom error handler if provided
      if (handlers?.onQueryError && isApiError(error)) {
        handlers.onQueryError(error);
      }
    },
  });
}

/**
 * Creates a mutation cache with error handling
 *
 * @param handlers - Error handler callbacks
 * @returns Configured MutationCache instance
 */
function createMutationCache(handlers?: QueryClientErrorHandlers): MutationCache {
  return new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      // Log error in development
      if (process.env.NODE_ENV === 'development') {
        console.error(`[MutationCache] Mutation failed:`, {
          mutationKey: mutation.options.mutationKey,
          error,
        });
      }

      // Handle authentication errors
      if (isAuthError(error) && handlers?.onAuthError) {
        handlers.onAuthError();
        return;
      }

      // Call custom error handler if provided
      if (handlers?.onMutationError && isApiError(error)) {
        handlers.onMutationError(error);
      }
    },
  });
}

// ============================================================================
// Query Client Configuration
// ============================================================================

/**
 * Configuration options for creating a QueryClient
 */
export interface QueryClientOptions {
  /** Custom stale time for queries (default: STALE_TIMES.DEFAULT) */
  staleTime?: number;
  /** Custom gc time for queries (default: GC_TIMES.DEFAULT) */
  gcTime?: number;
  /** Custom retry count (default: MAX_RETRY_ATTEMPTS) */
  retryCount?: number;
  /** Whether to refetch on window focus (default: true) */
  refetchOnWindowFocus?: boolean;
  /** Whether to refetch on reconnect (default: true) */
  refetchOnReconnect?: boolean;
  /** Error handlers for global error handling */
  errorHandlers?: QueryClientErrorHandlers;
}

/**
 * Creates a configured QueryClient instance with sensible defaults
 * for the School iDB application.
 *
 * Features:
 * - Appropriate stale times for different data types
 * - Retry logic using isRetryableError from utils
 * - Exponential backoff with jitter for retries
 * - Proper garbage collection times
 * - Offline scenario handling (refetch on reconnect)
 * - Global error handling integration
 *
 * @param options - Optional configuration overrides
 * @returns Configured QueryClient instance
 *
 * @example
 * ```typescript
 * // Create with defaults
 * const queryClient = createQueryClient();
 *
 * // Create with custom error handlers
 * const queryClient = createQueryClient({
 *   errorHandlers: {
 *     onQueryError: (error) => showToast(error.message),
 *     onAuthError: () => redirectToLogin(),
 *   },
 * });
 * ```
 */
export function createQueryClient(options?: QueryClientOptions): QueryClient {
  const {
    staleTime = STALE_TIMES.DEFAULT,
    gcTime = GC_TIMES.DEFAULT,
    retryCount = MAX_RETRY_ATTEMPTS,
    refetchOnWindowFocus = true,
    refetchOnReconnect = true,
    errorHandlers,
  } = options ?? {};

  return new QueryClient({
    queryCache: createQueryCache(errorHandlers),
    mutationCache: createMutationCache(errorHandlers),
    defaultOptions: {
      queries: {
        // Stale time - how long data is considered fresh
        staleTime,
        // Garbage collection time - how long inactive data stays in cache
        gcTime,
        // Retry configuration
        retry: (failureCount, error) => {
          // Respect custom retry count
          if (failureCount >= retryCount) {
            return false;
          }
          return shouldRetryQuery(failureCount, error);
        },
        retryDelay: getRetryDelay,
        // Refetch behavior
        refetchOnWindowFocus,
        refetchOnReconnect,
        // Don't refetch on mount if data is fresh
        refetchOnMount: true,
        // Network mode - handle offline scenarios gracefully
        networkMode: 'offlineFirst',
        // Structural sharing for performance
        structuralSharing: true,
      },
      mutations: {
        // Retry mutations only on network errors
        retry: (failureCount, error) => {
          // Mutations should be more conservative with retries
          if (failureCount >= 1) {
            return false;
          }
          // Only retry on network errors for mutations
          return isApiError(error) && error.code === 'NETWORK_ERROR';
        },
        retryDelay: getRetryDelay,
        // Network mode for mutations
        networkMode: 'offlineFirst',
      },
    },
  });
}

// ============================================================================
// Default Query Client Instance
// ============================================================================

/**
 * Default QueryClient instance
 * Lazily initialized on first access
 */
let defaultQueryClient: QueryClient | null = null;

/**
 * Gets the default QueryClient instance.
 * Creates one with default configuration if it doesn't exist.
 *
 * @returns The default QueryClient instance
 *
 * @example
 * ```typescript
 * const queryClient = getQueryClient();
 * ```
 */
export function getQueryClient(): QueryClient {
  if (!defaultQueryClient) {
    defaultQueryClient = createQueryClient();
  }
  return defaultQueryClient;
}

/**
 * Configures the default QueryClient with custom options.
 * This should be called early in the application lifecycle,
 * before any queries are made.
 *
 * @param options - Configuration options
 *
 * @example
 * ```typescript
 * configureQueryClient({
 *   errorHandlers: {
 *     onAuthError: () => {
 *       // Redirect to login
 *       window.location.href = '/auth/login';
 *     },
 *   },
 * });
 * ```
 */
export function configureQueryClient(options: QueryClientOptions): void {
  defaultQueryClient = createQueryClient(options);
}

/**
 * Resets the default QueryClient instance.
 * Useful for testing or when configuration needs to change.
 */
export function resetQueryClient(): void {
  if (defaultQueryClient) {
    defaultQueryClient.clear();
  }
  defaultQueryClient = null;
}

// ============================================================================
// Query Key Helpers
// ============================================================================

/**
 * Creates a query key factory for a specific entity type.
 * Provides consistent query key structure across the application.
 *
 * @param entity - The entity name (e.g., 'schools', 'users')
 * @returns Query key factory object
 *
 * @example
 * ```typescript
 * const schoolKeys = createQueryKeyFactory('schools');
 *
 * // Use in queries
 * useQuery({
 *   queryKey: schoolKeys.detail('school-123'),
 *   queryFn: () => fetchSchool('school-123'),
 * });
 *
 * // Invalidate all school queries
 * queryClient.invalidateQueries({ queryKey: schoolKeys.all });
 * ```
 */
export function createQueryKeyFactory<T extends string>(entity: T) {
  return {
    /** Base key for all queries of this entity */
    all: [entity] as const,
    /** Key for list queries */
    lists: () => [entity, 'list'] as const,
    /** Key for a specific list with filters */
    list: (filters: Record<string, unknown>) => [entity, 'list', filters] as const,
    /** Key for detail queries */
    details: () => [entity, 'detail'] as const,
    /** Key for a specific detail */
    detail: (id: string) => [entity, 'detail', id] as const,
  };
}

/**
 * Pre-defined query key factories for common entities
 */
export const queryKeys = {
  users: createQueryKeyFactory('users'),
  schools: createQueryKeyFactory('schools'),
  feeds: createQueryKeyFactory('feeds'),
  reviews: createQueryKeyFactory('reviews'),
  aiSearch: createQueryKeyFactory('ai-search'),
  savedSchools: createQueryKeyFactory('saved-schools'),
  claims: createQueryKeyFactory('claims'),
  config: createQueryKeyFactory('config'),
} as const;

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Invalidates all queries for a specific entity.
 *
 * @param queryClient - The QueryClient instance
 * @param entity - The entity to invalidate
 *
 * @example
 * ```typescript
 * // Invalidate all school queries after a mutation
 * invalidateEntity(queryClient, 'schools');
 * ```
 */
export function invalidateEntity(queryClient: QueryClient, entity: keyof typeof queryKeys): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: queryKeys[entity].all });
}

/**
 * Prefetches data for a query.
 * Useful for prefetching data on hover or before navigation.
 *
 * @param queryClient - The QueryClient instance
 * @param queryKey - The query key
 * @param queryFn - The query function
 * @param staleTime - Optional stale time override
 *
 * @example
 * ```typescript
 * // Prefetch school data on hover
 * onMouseEnter={() => {
 *   prefetchQuery(
 *     queryClient,
 *     queryKeys.schools.detail(schoolId),
 *     () => fetchSchool(schoolId),
 *     STALE_TIMES.SCHOOL
 *   );
 * }}
 * ```
 */
export async function prefetchQuery<T>(
  queryClient: QueryClient,
  queryKey: readonly unknown[],
  queryFn: () => Promise<T>,
  staleTime?: number
): Promise<void> {
  await queryClient.prefetchQuery({
    queryKey,
    queryFn,
    staleTime: staleTime ?? STALE_TIMES.DEFAULT,
  });
}
